import React, {useEffect, useRef, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {ArrowUp, ArrowDown, Pencil, X, LogOut, Check} from 'lucide-react';
import {staffApi} from '@/lib/staffApi';
import './staff-more-menu.css';

export function orderedMenuItems(items, order) {
    const ids = Array.isArray(order) ? [...new Set(order)] : [];
    return [...ids.map(id => items.find(item => item.id === id)).filter(Boolean), ...items.filter(item => !ids.includes(item.id))];
}

export function StaffMoreMenu({items, onClose, onLogout}) {
    const [saved, setSaved] = useState(items.map(item => item.id));
    const [draft, setDraft] = useState(saved);
    const [version, setVersion] = useState(0);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [reload, setReload] = useState(0);
    const dialog = useRef(null), mounted = useRef(true), savingRef = useRef(false), closeRef = useRef(onClose);
    closeRef.current = onClose;
    savingRef.current = saving;

    useEffect(() => {
        let active = true;
        setLoading(true); setError('');
        staffApi.moreMenuOrder().then(result => {
            if (!active) return;
            const order = orderedMenuItems(items, result.order).map(item => item.id);
            setSaved(order); setDraft(order); setVersion(result.version); setEditing(false);
        }).catch(() => { if (active) setError('Your saved menu order could not be loaded. Please try again.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [items, reload]);

    useEffect(() => {
        mounted.current = true;
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.current?.focus();
        const keydown = event => {
            if (event.key === 'Escape') {
                event.preventDefault(); if (!savingRef.current) closeRef.current();
            }
            if (event.key === 'Tab') {
                const controls = [...dialog.current.querySelectorAll('button:not(:disabled), a[href]')];
                const first = controls[0], last = controls[controls.length - 1];
                if (!first) { event.preventDefault(); return; }
                const outside = !dialog.current.contains(document.activeElement);
                if (event.shiftKey && (outside || document.activeElement === first || document.activeElement === dialog.current)) {
                    event.preventDefault(); last.focus();
                } else if (!event.shiftKey && (outside || document.activeElement === last || document.activeElement === dialog.current)) {
                    event.preventDefault(); first.focus();
                }
            }
        };
        document.addEventListener('keydown', keydown);
        return () => {
            mounted.current = false;
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', keydown);
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, []);

    const move = (id, direction) => {
        setDraft(current => {
            const next = [...current], index = next.indexOf(id), target = index + direction;
            if (index < 0 || target < 0 || target >= next.length) return current;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };
    const cancel = () => { setDraft(saved); setEditing(false); setError(''); };
    const save = async () => {
        if (savingRef.current) return;
        savingRef.current = true; setSaving(true); setError('');
        try {
            const result = await staffApi.saveMoreMenuOrder(draft, version);
            if (!mounted.current) return;
            const order = orderedMenuItems(items, result.order).map(item => item.id);
            setSaved(order); setDraft(order); setVersion(result.version); setEditing(false); setNotice('Menu order saved.');
        } catch (err) {
            if (mounted.current) setError(err.message || 'Your order could not be saved. Please try again.');
        } finally {
            savingRef.current = false; if (mounted.current) setSaving(false);
        }
    };
    const shown = orderedMenuItems(items, editing ? draft : saved);
    const changed = draft.join('|') !== saved.join('|');

    return <div className="staff-more-overlay" data-testid="mobile-more-sheet">
        <div className="staff-more-backdrop" onClick={() => { if (!saving) onClose(); }} aria-hidden="true" />
        <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="staff-more-title" className="staff-more-panel">
            <header className="staff-more-header">
                <div className="staff-more-heading">
                    <h2 id="staff-more-title">All sections</h2>
                    {!editing && <button type="button" className="staff-more-icon-button" aria-label="Edit section order" data-testid="more-edit-order"
                        disabled={loading || !!error} onClick={() => { setDraft(saved); setEditing(true); setNotice(''); }}><Pencil size={18}/></button>}
                </div>
                <button type="button" className="staff-more-icon-button" onClick={onClose} disabled={saving} data-testid="mobile-more-close" aria-label="Close"><X size={22}/></button>
            </header>
            <div className="staff-more-content">
                {editing && <p className="staff-more-help">Move sections up or down, then save your order.</p>}
                {error && <div role="alert" className="staff-more-error">{error}<button type="button" disabled={saving || loading} onClick={() => setReload(n => n + 1)}>Reload saved order</button></div>}
                {notice && <p role="status" className="staff-more-notice"><Check size={16}/>{notice}</p>}
                {editing ? <ol className="staff-more-order-list" data-testid="more-order-list">
                    {shown.map((item, index) => <li key={item.id} data-testid={`more-order-${item.id}`}>
                        <item.icon className="staff-more-item-icon" size={21} strokeWidth={1.75}/>
                        <span className="staff-more-order-label">{item.label}</span>
                        <div className="staff-more-move-controls">
                            <button type="button" aria-label={`Move ${item.label} up`} disabled={index === 0 || saving} onClick={() => move(item.id, -1)}><ArrowUp size={19}/></button>
                            <button type="button" aria-label={`Move ${item.label} down`} disabled={index === shown.length - 1 || saving} onClick={() => move(item.id, 1)}><ArrowDown size={19}/></button>
                        </div>
                    </li>)}
                </ol> : <div className="staff-more-grid" data-testid="more-sections-grid">
                    {shown.map(item => <NavLink key={item.id} to={item.to} end={item.end} onClick={onClose} data-testid={`more-nav-${item.id}`}
                        className={({isActive}) => `staff-more-tile${isActive ? ' is-active' : ''}`}>
                        <item.icon className="staff-more-item-icon" size={24} strokeWidth={1.75}/>
                        <span>{item.label}</span>
                    </NavLink>)}
                </div>}
            </div>
            <footer className="staff-more-footer">
                {editing ? <>
                    <button type="button" className="staff-more-secondary" disabled={saving} onClick={cancel}>Cancel</button>
                    <button type="button" className="staff-more-save" data-testid="more-save-order" disabled={saving || !changed} onClick={save}>{saving ? 'Saving…' : 'Save order'}</button>
                </> : <button type="button" className="staff-more-signout" onClick={() => { onClose(); onLogout(); }} data-testid="more-sign-out"><LogOut size={19}/>Sign out</button>}
            </footer>
        </section>
    </div>;
}
